import styled from "@emotion/styled";
import type { ReactNode } from "react";
import { Button } from "react-aria-components";

import {
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { decodeAgentDisplayText } from "@/pages/agent/utils/agent-display-text.utils";

export const AgentRelationshipCard = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <StyledCard>
    <MDSText
      color={MDSTextColor.Primary}
      variant={MDSTextVariant.HeaderS}
      weight={MDSTextWeight.SemiBold}
    >
      {title}
    </MDSText>
    <StyledGroups>{children}</StyledGroups>
  </StyledCard>
);

export const AgentRelationshipSection = styled.div`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
`;

export const AgentRelationshipGroup = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <StyledGroup>
    <AgentRelationshipLabel>{title}</AgentRelationshipLabel>
    {children}
  </StyledGroup>
);

export const AgentRelationshipGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[2]};
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

export const AgentRelationshipTile = ({
  description,
  icon,
  label,
  onOpen,
  title,
}: {
  description?: string | null;
  icon: MDSIconKind;
  label: string;
  onOpen: () => void;
  title: string;
}) => {
  const displayTitle = decodeAgentDisplayText({ value: title });
  const displayDescription = description ? decodeAgentDisplayText({ value: description }) : null;

  return (
    <StyledTile aria-label={`Open ${label} ${displayTitle}`} onPress={onOpen}>
      <StyledTileIcon>
        <MDSIcon color={MDSIconColor.Secondary} kind={icon} size={15} />
      </StyledTileIcon>
      <StyledTileCopy>
        <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
          {label}
        </MDSText>
        <StyledTileTitle
          color={MDSTextColor.Primary}
          variant={MDSTextVariant.TextS}
          weight={MDSTextWeight.SemiBold}
        >
          {displayTitle}
        </StyledTileTitle>
        {displayDescription ? (
          <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextXs} numberOfLines={1}>
            {displayDescription}
          </MDSText>
        ) : null}
      </StyledTileCopy>
      <MDSIcon color={MDSIconColor.Secondary} kind={MDSIconKind.AngleRight} size={12} />
    </StyledTile>
  );
};

export const AgentRelationshipEmpty = ({ children = "None" }: { children?: ReactNode }) => (
  <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextS}>
    {children}
  </MDSText>
);

export const AgentRelationshipLabel = ({ children }: { children: ReactNode }) => (
  <MDSText
    color={MDSTextColor.Secondary}
    variant={MDSTextVariant.TextS}
    weight={MDSTextWeight.SemiBold}
  >
    {children}
  </MDSText>
);

const StyledCard = styled.section`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[5]};
`;

const StyledGroups = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[5]};
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const StyledGroup = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const StyledTile = styled(Button)`
  appearance: none;
  align-items: center;
  background: ${({ theme }) => theme.colors.background.card.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  color: inherit;
  cursor: pointer;
  display: grid;
  font: inherit;
  gap: ${({ theme }) => theme.spacing[3]};
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-height: 82px;
  min-width: 0;
  padding: ${({ theme }) => theme.spacing[3]};
  text-align: left;

  &[data-hovered]:not([data-disabled]) {
    background: ${({ theme }) => theme.colors.background.card.emphasis};
  }

  &[data-pressed]:not([data-disabled]) {
    transform: translateY(1px);
  }

  &[data-focus-visible] {
    outline: 2px solid ${({ theme }) => theme.colors.border.emphasis};
    outline-offset: 2px;
  }
`;

const StyledTileIcon = styled.span`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.button.toggle.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  display: inline-flex;
  height: 30px;
  justify-content: center;
  width: 30px;
`;

const StyledTileCopy = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const StyledTileTitle = styled(MDSText)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
